import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { TicketDetail, TimelineEntry } from '@aurora/contracts';
import { get, patch, type ApiError } from '../../lib/api';
import { useToast } from '../../components/toast';

/**
 * The data behind one ticket's screen, and the one way to change it.
 *
 * Every panel on the screen reads the ticket through the same cache entry, so a
 * change made in the header shows in the rail at once, and every change goes
 * through `useTicketUpdate` — so each one carries the version it was made
 * against, shows instantly, rolls back if refused, and says why.
 */

export type TicketResponse = { data: TicketDetail };

export interface TimelineResponse {
  data: TimelineEntry[];
  meta: { can_see_notes: boolean; actor_id?: number };
}

export const ticketKey = (number: string) => ['ticket', number] as const;

export function useTicket(number: string) {
  return useQuery<TicketResponse, ApiError>({
    queryKey: ticketKey(number),
    queryFn: () => get<TicketResponse>(`/tickets/${number}`),
  });
}

export function useTimeline(number: string) {
  return useQuery<TimelineResponse, ApiError>({
    queryKey: ['ticket-timeline', number],
    queryFn: () => get<TimelineResponse>(`/tickets/${number}/timeline`),
  });
}

/**
 * Everything on the screen that a change to the ticket can make stale.
 *
 * The rail's panels are mounted for as long as the ticket is open, so nothing
 * else would ever refetch them: a reply's file never reached the attachments
 * card, and watchers copied in by a merge never reached the watchers card.
 */
export function refreshTicket(
  queryClient: QueryClient,
  number: string,
  also: Array<'links' | 'watchers' | 'tasks' | 'items'> = [],
): void {
  void queryClient.invalidateQueries({ queryKey: ticketKey(number) });
  void queryClient.invalidateQueries({ queryKey: ['ticket-timeline', number] });
  void queryClient.invalidateQueries({ queryKey: ['ticket-attachments', number] });
  // Every list the ticket could appear in, with whatever filters it had.
  void queryClient.invalidateQueries({ queryKey: ['tickets'] });
  for (const panel of also) {
    void queryClient.invalidateQueries({ queryKey: [`ticket-${panel}`, number] });
  }
}

export interface TicketChange {
  /** What to send, as `PATCH /tickets/:number` takes it. The version is added. */
  body: Record<string, unknown>;
  /** Shown at once and put back if the server refuses. */
  optimistic?: Partial<TicketDetail>;
  /** The toast on success. Leave it out when the change is plainly visible in place. */
  success?: string;
  /** For a caller that shows the failure itself, next to the fields it names. */
  silent?: boolean;
}

/**
 * Changes the ticket.
 *
 * Changes run one after another (`scope`), each reading the version from the
 * cache when it is sent — so two quick edits by the same agent never refuse
 * each other as "changed by somebody else", while an edit made against a
 * version somebody *else* has since replaced is refused, the latest version is
 * fetched, and the agent is told to make it again rather than silently
 * undoing the other person's change.
 */
export function useTicketUpdate(number: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const key = ticketKey(number);

  return useMutation<TicketResponse, ApiError, TicketChange, { previous: TicketResponse | undefined }>({
    scope: { id: `ticket-update-${number}` },
    mutationFn: ({ body }) => {
      const version = queryClient.getQueryData<TicketResponse>(key)?.data.version;
      return patch<TicketResponse>(`/tickets/${number}`, version === undefined ? body : { ...body, version });
    },
    onMutate: async ({ optimistic }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TicketResponse>(key);
      if (previous && optimistic) {
        queryClient.setQueryData<TicketResponse>(key, { ...previous, data: { ...previous.data, ...optimistic } });
      }
      return { previous };
    },
    onError: (error, change, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      if (change.silent) return;
      if (error.code === 'stale_write') {
        toast.error(
          'Somebody else changed this ticket a moment ago',
          'The latest version is shown now. Make your change again if it is still needed.',
        );
      } else {
        toast.error(error);
      }
    },
    onSuccess: (response, change) => {
      // The edit answers with the ticket as it now is; the SLA timers are not
      // part of that answer, so the ones on screen stay until the refetch.
      queryClient.setQueryData<TicketResponse>(key, (old) => ({
        data: { ...response.data, sla_timers: response.data.sla_timers ?? old?.data.sla_timers },
      }));
      if (change.success) toast.success(change.success);
    },
    onSettled: () => refreshTicket(queryClient, number),
  });
}

/** The statuses a ticket can be moved to, with the one a "Resolve" means. */
export function firstOfCategory<T extends { category: string; position?: number }>(
  statuses: T[] | undefined,
  category: 'open' | 'pending' | 'resolved' | 'closed',
): T | undefined {
  return (statuses ?? [])
    .filter((status) => status.category === category)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))[0];
}

/** Choices for the fields the directory does not list — type and source. */
export function useTicketChoices(enabled = true) {
  return useQuery<
    { fields: Array<{ field: string; choices?: Array<{ value: string; label: string }> }> },
    ApiError,
    { type: Array<{ value: string; label: string }>; source: Array<{ value: string; label: string }> }
  >({
    // A key of its own: the list screen reads the same endpoint for its filter
    // panel, and two screens writing different shapes into one key is how one
    // of them ends up rendering the other's data.
    queryKey: ['ticket-detail', 'choices'],
    queryFn: () => get('/tickets/filters'),
    select: (response) => ({
      type: response.fields.find((field) => field.field === 'type')?.choices ?? [],
      source: response.fields.find((field) => field.field === 'source')?.choices ?? [],
    }),
    staleTime: 5 * 60_000,
    enabled,
  });
}
